import download from 'downloadjs'
import { Component, Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dimmer, Loader } from 'semantic-ui-react'
import { createGlobalStyle } from 'styled-components'

import { Icon, PDFViewer } from '@/ui'
import mime from 'mime'
import {
  isAudioFile,
  isFileMSG,
  isImageFile,
  isSupportedFile,
  isVideoFile,
} from './helpers'
import MsgPreview from './MsgPreview'
import PreviewErrorHandler from './PreviewErrorHandler'
import { StyledFilePreview } from './Styles'
import {
  ErrorBoundaryProps,
  ErrorBoundaryState,
  FilePreviewProps,
  FileViewProps,
  GlobalStyleProps,
} from './types'

const GlobalStyle = createGlobalStyle<GlobalStyleProps>`
  ${({ theme }) => theme.mediaQueries.mobile} {
    .close {
      color: ${props =>
        props.whiteColor ? 'white !important' : 'rgba(0,0,0,.87) !important'};
    }  
  }
`

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state = {
    error: false,
  }

  componentDidCatch(error: Error) {
    console.log(error)
    this.setState({ error: true })
  }

  render() {
    if (this.state.error) {
      return <div>{this.props.errorComponent}</div>
    }
    return this.props.children
  }
}

const FilePreview = ({
  filePath,
  fileName,
  mimeType,
  fileType,
  className,
  noPageHeader,
  onDownload,
  storageId,
}: FilePreviewProps) => {
  const { t } = useTranslation()

  const [toolbar, setToolbar] = useState(false)
  const [touchDevice, setTouchDevice] = useState(false)
  const [pdf] = useState(
    fileType.toLowerCase() === 'pdf'
      ? {
          fileName: fileName,
          filePath: filePath.replace(
            'data:application/octet-stream;base64,',
            '',
          ),
          mimeType: mime.getType('pdf'),
        }
      : null,
  )

  const container = useRef(null)

  const isTouchDevice = () => {
    setTouchDevice(true)
  }

  const handleDownload = () => {
    if (onDownload) {
      return onDownload()
    }
    download(filePath, fileName, mimeType)
  }

  useEffect(() => {
    window.addEventListener('touchstart', isTouchDevice)

    return () => {
      window.removeEventListener('touchstart', isTouchDevice)
    }
  }, [])

  const toggleToolbar = () => {
    setToolbar(!toolbar)
  }

  const showToolbar = !pdf && isSupportedFile(fileType)

  return (
    <StyledFilePreview
      ref={container}
      isImage={isImageFile(fileType)}
      onMouseEnter={() => {
        if (!touchDevice && showToolbar && !toolbar) {
          toggleToolbar()
        }
      }}
      onMouseLeave={() => {
        if (!touchDevice && showToolbar && toolbar) {
          toggleToolbar()
        }
      }}
      onClick={() => {
        if (touchDevice) {
          toggleToolbar()
        }
      }}
      className={className}>
      <div className="overflow-container">
        {showToolbar && toolbar && (
          <div
            className="actions-overflow"
            onClick={e => {
              if (toolbar && touchDevice) {
                e.stopPropagation()
              }
            }}>
            <div className="document-actions show-toolbar">
              <GlobalStyle whiteColor={toolbar} />
              <div />
              <button
                onClick={() => {
                  handleDownload()
                }}>
                {t('Download')} <Icon name="icon-download" />
              </button>
            </div>
          </div>
        )}
        <Suspense
          fallback={
            <Dimmer active inverted>
              <Loader>
                {t('Loading')} {fileName}
              </Loader>
            </Dimmer>
          }>
          <ErrorBoundary
            errorComponent={
              <PreviewErrorHandler
                errorType="error"
                handleDownload={handleDownload}
                t={t}
                fileType={fileType}
              />
            }>
            <FileView
              filePath={filePath}
              fileName={fileName}
              mimeType={mimeType}
              fileType={fileType}
              noPageHeader={noPageHeader}
              onDownload={onDownload}
              storageId={storageId}
              pdf={pdf}
              handleDownload={handleDownload}
            />
          </ErrorBoundary>
        </Suspense>
      </div>
    </StyledFilePreview>
  )
}

const FileView = ({
  filePath,
  fileName,
  mimeType,
  fileType,
  noPageHeader,
  onDownload,
  storageId,
  pdf,
  handleDownload,
}: FileViewProps) => {
  const { t } = useTranslation()

  if (pdf) {
    return (
      <div>
        <PDFViewer
          noPageHeader={noPageHeader}
          height="90vh"
          data={pdf}
          onDownload={onDownload}
          storageId={storageId}
        />
      </div>
    )
  }

  if (isImageFile(fileType)) {
    return (
      <div className="image-container">
        <img src={filePath} />
      </div>
    )
  }

  if (isFileMSG(fileType, mimeType)) {
    return <MsgPreview fileName={fileName} filePath={filePath} />
  }

  if (isAudioFile(fileType)) {
    return (
      <div className="audio-container">
        <audio controls>
          <source src={filePath} type={mimeType} />
        </audio>
      </div>
    )
  }

  if (isVideoFile(fileType)) {
    return (
      <div className="video-container">
        <video controls>
          <source src={filePath} type={mimeType} />
        </video>
      </div>
    )
  }

  return (
    <PreviewErrorHandler
      errorType="unsupported"
      handleDownload={handleDownload}
      t={t}
      fileType={fileType}
    />
  )
}

export default FilePreview
